// The sentence analyser's reading of a whole sentence: every word's analyses from the engine, the engine's sentence
// checker for roles and subject–verb agreement, and two small additions on top of it:
//   1. Words the lexicon does not know are set aside, so the rest of the sentence can still be checked.
//   2. Subjects joined by य / अ ("and", which follows the word it joins: बालओ य बाला य) count together as one plural
//      subject, because Prakrit has no dual. The checker reads one word at a time, so it would ask each singular
//      word for a singular verb; here those agreement notes are dropped when the verb is plural and every joined
//      word has the verb's person.
// As in the कारक tool, a neuter word such as फलं (प्रथमा = द्वितीया) is shown as कर्म when another word is the clear कर्ता.

import type { Analysis } from '@/prakrit/lib/grammar/analyser'
import type { Issue, Role } from '@/prakrit/lib/grammar/sentence'
import type { Case, Lang } from '@/prakrit/lib/grammar/types'

import { analyse, isFinite, personOf } from '@/prakrit/lib/grammar/analyser'
import { isDevanagari, tokenize } from '@/prakrit/lib/grammar/devanagari'
import { checkSentence } from '@/prakrit/lib/grammar/sentence'
import { AVYAYA } from '@/prakrit/lib/grammar/lexicon'
import { readingLabel } from '@/prakrit/components/grammar/vakya/vakya-labels'

export const MAX_WORDS = 16

export const MAX_CHARS = 300

export interface VakyaWord {
    token: string
    /** Every analysis the engine has for this form. */
    analyses: Analysis[]
    /** The readings offered as chips: real forms only (a bare stem or root is left out when a form exists), one per label. */
    readings: Analysis[]
    /** The reading the sentence picks (one of `readings`), null for a word the lexicon does not know. */
    chosen: Analysis | null
    role: Role | null
}

export type Agreement =
    | { kind: 'empty' }
    | { kind: 'unknown' }
    | { kind: 'cannot', issue: Issue }
    | { kind: 'clash', issue: Issue }
    | { kind: 'agree', subject: boolean }

export interface VakyaParse {
    words: VakyaWord[]
    /** Words found in the engine's tables. */
    recognised: number
    agreement: Agreement
    /** Extra notes: variant forms and joined subjects. */
    notes: Lang[]
    /** Words left out of the check because the lexicon does not know them. */
    skipped: string[]
    /** Some words are not in Devanagari. */
    latin: boolean
    /** More than MAX_WORDS words were typed; only the first ones are analysed. */
    truncated: boolean
    /** Indices (into `words`) of subjects joined by य / अ. */
    joined: number[]
    /** Index (into `words`) of the main verb, -1 when there is none. */
    verb: number
}

/** The अव्यय entry meaning "and" (अ / य). */
const AND = AVYAYA.find(([, , en]) => en === 'and')?.[0] ?? null

export const isAnd = (w: { analyses: Analysis[] }): boolean => AND !== null && w.analyses.some(a => a.pos === 'avyaya' && a.lemma === AND)

const hasCase = (a: Analysis, c: Case): boolean => (a.pos === 'noun' || a.pos === 'pronoun') && a.case === c

const nominatives = (list: Analysis[]): Analysis[] => list.filter(a => hasCase(a, 'prathama'))

/** Readings for the chips, and the chosen reading mapped onto them. */
const settle = (analyses: Analysis[], chosen: Analysis | null): { readings: Analysis[], chosen: Analysis | null } => {
    const real = analyses.filter(a => a.pos !== 'stem' && a.pos !== 'dhatu')
    const pool = real.length > 0 ? real : analyses
    const readings: Analysis[] = []
    const labels = new Set<string>()

    for (const a of pool) {
        const label = readingLabel(a)

        if (labels.has(label)) continue

        labels.add(label)
        readings.push(a)
    }

    if (!chosen) return { readings, chosen: readings[0] ?? null }
    if (readings.includes(chosen)) return { readings, chosen }

    const same = readings.findIndex(r => readingLabel(r) === readingLabel(chosen))

    if (same >= 0) {
        readings[same] = chosen

        return { readings, chosen }
    }

    return { readings, chosen: readings[0] ?? null }
}

const listText = (items: string[], and: string): string => (items.length <= 2
    ? items.join(` ${and} `)
    : `${items.slice(0, -1).join(', ')} ${and} ${items[items.length - 1]}`)

const EMPTY: VakyaParse = {
    words: [],
    recognised: 0,
    agreement: { kind: 'empty' },
    notes: [],
    skipped: [],
    latin: false,
    truncated: false,
    joined: [],
    verb: -1
}

export const parseVakya = (text: string): VakyaParse => {
    const all = tokenize(text.slice(0, MAX_CHARS))
    const tokens = all.slice(0, MAX_WORDS)

    if (tokens.length === 0) return EMPTY

    const analyses = tokens.map(t => analyse(t))
    const known = tokens.map((_, i) => i).filter(i => analyses[i].length > 0)
    const base = {
        recognised: known.length,
        skipped: tokens.filter((_, i) => analyses[i].length === 0),
        latin: tokens.some(t => !isDevanagari(t)),
        truncated: all.length > MAX_WORDS
    }

    if (known.length === 0) {
        return {
            ...base,
            words: tokens.map(token => ({ token, analyses: [], readings: [], chosen: null, role: null })),
            agreement: { kind: 'unknown' },
            notes: [],
            joined: [],
            verb: -1
        }
    }

    const result = checkSentence(known.map(i => tokens[i]))
    const list = known.map(i => ({ token: tokens[i], analyses: analyses[i] }))
    const roles: (Role | null)[] = known.map((_, j) => result.tokens[j]?.role ?? null)
    const chosen: (Analysis | null)[] = known.map((_, j) => result.tokens[j]?.chosen ?? null)
    let issues = [...result.issues]
    const notes: Lang[] = result.notes.map(n => ({ hi: n.hi, en: n.en }))
    const verbJ = roles.indexOf('verb')
    const verb = verbJ >= 0 ? chosen[verbJ] : null
    let joined: number[] = []

    // Subjects joined by य / अ: the word before each "and"; with a single "and" (बालओ बाला य), the word before that too.
    if (verb && isFinite(verb) && verb.number === 'bahu') {
        const andAt = list.map((w, j) => (isAnd(w) ? j : -1)).filter(j => j > 0)
        const isCandidate = (j: number): boolean => j >= 0 && j !== verbJ && !andAt.includes(j) && nominatives(list[j].analyses).length > 0
        const members: number[] = []

        for (const k of andAt) {
            if (isCandidate(k - 1) && !members.includes(k - 1)) members.push(k - 1)
        }

        if (members.length === 1 && isCandidate(members[0] - 1)) members.unshift(members[0] - 1)

        const picks = members.map(j => nominatives(list[j].analyses).find(a => personOf(a) === verb.person) ?? null)

        if (members.length >= 2 && picks.every(Boolean)) {
            const memberTokens = new Set(members.map(j => list[j].token))

            issues = issues.filter(issue => !(
                (issue.code === 'agreement_number' || issue.code === 'agreement_person')
                && issue.token !== undefined
                && memberTokens.has(issue.token)
            ))

            members.forEach((j, k) => {
                roles[j] = 'subject'
                chosen[j] = picks[k]
            })

            joined = members

            const names = members.map(j => list[j].token)
            const and = list[andAt[0]].token

            notes.push({
                hi: `${listText(names, 'और')} ${and} से जुड़े हैं: ये मिलकर कर्ता हैं, इसलिए क्रिया बहुवचन में है।`,
                en: `${listText(names, 'and')} are joined by ${and} (and): together they are the subject, so the verb is plural.`
            })
        }
    }

    // Several कर्ता: keep the clear ones (and the joined ones); a word that can also be द्वितीया becomes कर्म.
    const subjects = roles.map((r, j) => (r === 'subject' ? j : -1)).filter(j => j >= 0)

    if (subjects.length > 1) {
        const clear = subjects.filter(j => joined.includes(j) || !list[j].analyses.some(a => hasCase(a, 'dvitiya')))
        const keep = clear.length > 0 ? clear : [subjects[0]]

        for (const j of subjects) {
            if (keep.includes(j)) continue

            const accusative = list[j].analyses.find(a => hasCase(a, 'dvitiya'))

            if (accusative) {
                roles[j] = 'object'
                chosen[j] = accusative
            }
        }
    }

    const words: VakyaWord[] = tokens.map(token => ({ token, analyses: [], readings: [], chosen: null, role: null }))

    known.forEach((i, j) => {
        const settled = settle(analyses[i], chosen[j])

        words[i] = { token: tokens[i], analyses: analyses[i], readings: settled.readings, chosen: settled.chosen, role: roles[j] }
    })

    const first = issues[0] ?? null
    let agreement: Agreement

    if (first && first.code === 'no_verb') agreement = { kind: 'cannot', issue: first }
    else if (first) agreement = { kind: 'clash', issue: first }
    else agreement = { kind: 'agree', subject: roles.includes('subject') }

    return {
        ...base,
        words,
        agreement,
        notes,
        joined: joined.map(j => known[j]),
        verb: verbJ >= 0 ? known[verbJ] : -1
    }
}

/** The same words in the same order (punctuation and spacing ignored). */
export const sameSentence = (a: string, b: string): boolean => tokenize(a).join(' ') === tokenize(b).join(' ')
